import { useMemo } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

export function CurrencyLabel({ currency, fontSize = '2xs' }) {
  return currency ? (
    <Text as="span" fontSize={fontSize} mr={1} color="redesign.text.muted">{currency}</Text>
  ) : (
    <Text as="span" fontSize={fontSize} verticalAlign="top">$</Text>
  );
}

function bucketMatches(bucket, categoryId) {
  return categoryId == null || bucket.category_id === categoryId;
}

export default function CategoryKeyMetrics({ yearlyMetrics, categoryId, typeFilter }) {
  const { t } = useTranslation('categories');
  const rows = useMemo(() => {
    if (!yearlyMetrics || yearlyMetrics.length === 0) return [];
    const now = new Date();

    const currencies = new Set();
    yearlyMetrics.forEach(ym => {
      ym.data.forEach(d => {
        if (bucketMatches(d, categoryId) && d.currency) currencies.add(d.currency);
      });
    });
    const currencyList = [...currencies].sort();
    const isMulti = currencyList.length > 1;

    const perYear = yearlyMetrics.map((ym) => {
      const months = ym.year === now.getFullYear() ? now.getMonth() + 1 : 12;
      const rowsForYear = [];
      if (isMulti) {
        currencyList.forEach(c => {
          const total = ym.data
            .filter(d => bucketMatches(d, categoryId) && (d.currency || 'COP') === c)
            .reduce((sum, d) => sum + Math.abs(d.total || 0), 0);
          if (total > 0) {
            rowsForYear.push({ key: `${ym.year}-${c}`, year: ym.year, currency: c, total, avg: months > 0 ? total / months : 0 });
          }
        });
      } else {
        const total = ym.data
          .filter(d => bucketMatches(d, categoryId))
          .reduce((sum, d) => sum + Math.abs(d.total || 0), 0);
        if (total > 0) {
          rowsForYear.push({ key: `${ym.year}`, year: ym.year, currency: currencyList[0] || null, total, avg: months > 0 ? total / months : 0 });
        }
      }
      return { year: ym.year, rows: rowsForYear };
    });

    const hasAnyData = perYear.some(y => y.rows.length > 0);
    if (!hasAnyData) return [];

    const defaultCurrency = currencyList[0] || null;
    const out = [];
    perYear.forEach(y => {
      if (y.rows.length > 0) {
        out.push(...y.rows);
      } else {
        out.push({ key: `${y.year}-empty`, year: y.year, currency: defaultCurrency, total: 0, avg: 0 });
      }
    });
    return out;
  }, [yearlyMetrics, categoryId]);

  if (rows.length === 0) return null;

  return (
    <Box>
      <Flex align="center" mb={2}>
        <Text textStyle="microLabel">{t('keyMetrics.title')}</Text>
        <Text flex={1} textStyle="microLabel" textAlign="right" pr={6}>
          {typeFilter === 'income' ? t('keyMetrics.earnedPerYear') : t('keyMetrics.spentPerYear')}
        </Text>
        <Text w="140px" textStyle="microLabel" textAlign="right">
          {typeFilter === 'income' ? t('keyMetrics.avgMonthlyEarnings') : t('keyMetrics.avgMonthlySpend')}
        </Text>
      </Flex>
      {rows.map((r, i) => {
        const showYear = i === 0 || rows[i - 1].year !== r.year;
        return (
          <Flex key={r.key} align="center" py={2}>
            <Text w="48px" fontSize="sm" fontWeight="600" color="redesign.text.muted">{showYear ? r.year : ''}</Text>
            <Text flex={1} fontSize="sm" fontWeight="600" color="redesign.heading" textAlign="right" pr={6}>
              <CurrencyLabel currency={r.currency} />
              {r.total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Text>
            <Text w="140px" fontSize="sm" fontWeight="600" color="redesign.heading" textAlign="right">
              <CurrencyLabel currency={r.currency} />
              {r.avg.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Text>
          </Flex>
        );
      })}
    </Box>
  );
}
